import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { useWebsiteHeroImages, type WebsiteHeroImage } from '../hooks/useWebsiteHeroImages';
import { useWebsiteAbout } from '../hooks/useWebsiteAbout';
import { useWebsiteFeaturedSeries } from '../hooks/useWebsiteFeaturedSeries';
import { useWebsiteMinistryImages, MINISTRY_IMAGE_KEYS } from '../hooks/useWebsiteMinistryImages';
import { useSermonSeries } from '../hooks/useSermonSeries';
import { supabase } from '../services/supabase';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.25rem;
  color: var(--onSurface);
`;

const AddRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const UploadButtonLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--primary);
  color: var(--onPrimary);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: var(--primaryDark);
  }
  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  input {
    display: none;
  }
`;

const DeleteButton = styled.button`
  padding: 0.35rem 0.75rem;
  background: var(--error);
  color: var(--onPrimary);
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  &:hover {
    background: var(--error);
  }
`;

const ImageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ImageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const Thumb = styled.div<{ $src: string }>`
  width: 120px;
  height: 68px;
  background: var(--outline) url(${({ $src }) => $src}) center/cover;
  border-radius: 6px;
  flex-shrink: 0;
`;

const ImageUrl = styled.a`
  flex: 1;
  font-size: 0.9rem;
  color: var(--primary);
  word-break: break-all;
  &:hover {
    text-decoration: underline;
  }
`;

const OrderLabel = styled.span`
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
  min-width: 3rem;
`;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

async function uploadHeroImage(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const fileName = `website-hero/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error: uploadError } = await supabase.storage
    .from('post-images')
    .upload(fileName, file, { upsert: false });
  if (uploadError) throw new Error(uploadError.message);
  const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
  return data.publicUrl;
}

async function uploadAboutImage(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const fileName = `website-about/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error: uploadError } = await supabase.storage
    .from('post-images')
    .upload(fileName, file, { upsert: false });
  if (uploadError) throw new Error(uploadError.message);
  const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
  return data.publicUrl;
}

async function uploadMinistryImage(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const fileName = `website-ministries/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
  const { error: uploadError } = await supabase.storage
    .from('post-images')
    .upload(fileName, file, { upsert: false });
  if (uploadError) throw new Error(uploadError.message);
  const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
  return data.publicUrl;
}

const TextArea = styled.textarea`
  width: 100%;
  min-height: 160px;
  padding: 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
`;

const SaveButton = styled.button`
  padding: 0.5rem 1.25rem;
  background: var(--success);
  color: var(--onPrimary);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--primaryDark);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormField = styled.div`
  margin-bottom: 1.25rem;
`;

function HeroImagesTab() {
  const {
    images,
    loading,
    error,
    addImage,
    addLoading,
    deleteImage,
    deleteLoading,
    reorder,
  } = useWebsiteHeroImages();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    if (!file.type.startsWith('image/') || !ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Please choose a JPEG, PNG, WebP, or GIF image.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('Image must be smaller than 5MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      const url = await uploadHeroImage(file);
      await addImage(url);
    } catch (err) {
      console.error(err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (row: WebsiteHeroImage) => {
    if (!window.confirm('Remove this hero image?')) return;
    setDeletingId(row.id);
    try {
      await deleteImage(row.id);
    } catch (e) {
      console.error(e);
      alert('Failed to delete.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleMove = async (row: WebsiteHeroImage, direction: 'up' | 'down') => {
    const idx = images.findIndex((i) => i.id === row.id);
    if (idx < 0) return;
    const newOrder = direction === 'up' ? idx - 1 : idx + 1;
    if (newOrder < 0 || newOrder >= images.length) return;
    const target = images[newOrder];
    try {
      await reorder({ id: row.id, sort_order: target.sort_order });
      await reorder({ id: target.id, sort_order: row.sort_order });
    } catch (e) {
      console.error(e);
    }
  };

  if (error) {
    return <p>Error loading hero images. You may need to run the database migration.</p>;
  }

  return (
    <>
      <SectionTitle>Hero images</SectionTitle>
      <p style={{ margin: '0 0 1rem', color: 'var(--onSurfaceVariant)', fontSize: '0.9rem' }}>
        These images rotate as the background on the public site homepage hero. Upload images (JPEG, PNG, WebP, or GIF, max 5MB). Order determines the rotation order.
      </p>
      <AddRow>
        <UploadButtonLabel className={addLoading ? 'disabled' : ''}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFileChange}
            disabled={addLoading}
          />
          {addLoading ? 'Uploading…' : 'Upload image'}
        </UploadButtonLabel>
        {uploadError && (
          <span style={{ color: 'var(--error)', fontSize: '0.9rem' }}>{uploadError}</span>
        )}
      </AddRow>
      {loading ? (
        <p>Loading…</p>
      ) : images.length === 0 ? (
        <p style={{ color: 'var(--onSurfaceVariant)' }}>No hero images yet. Add one above.</p>
      ) : (
        <ImageList>
          {images.map((row, idx) => (
            <ImageRow key={row.id}>
              <Thumb $src={row.url} />
              <OrderLabel>{idx + 1}</OrderLabel>
              <ImageUrl href={row.url} target="_blank" rel="noopener noreferrer">
                {row.url}
              </ImageUrl>
              <span style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => handleMove(row, 'up')}
                  disabled={idx === 0}
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                >
                  Up
                </button>
                <button
                  type="button"
                  onClick={() => handleMove(row, 'down')}
                  disabled={idx === images.length - 1}
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                >
                  Down
                </button>
                <DeleteButton
                  onClick={() => handleDelete(row)}
                  disabled={deleteLoading && deletingId === row.id}
                >
                  Remove
                </DeleteButton>
              </span>
            </ImageRow>
          ))}
        </ImageList>
      )}
    </>
  );
}

function AboutTab() {
  const { about, loading, error, update, updateLoading } = useWebsiteAbout();
  const [homeBlurb, setHomeBlurb] = useState('');
  const [aboutImageUrl, setAboutImageUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (about) {
      setHomeBlurb(about.home_blurb ?? '');
      setAboutImageUrl(about.background_image_url ?? null);
    }
  }, [about]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    if (!file.type.startsWith('image/') || !ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Please choose a JPEG, PNG, WebP, or GIF image.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('Image must be smaller than 5MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    try {
      const url = await uploadAboutImage(file);
      setAboutImageUrl(url);
    } catch (err) {
      console.error(err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaveMessage(null);
    try {
      await update({
        background_image_url: aboutImageUrl ?? null,
        home_blurb: homeBlurb.trim() || null,
      });
      setSaveMessage('About section saved.');
    } catch (err) {
      console.error(err);
      setSaveMessage('Failed to save.');
    }
  };

  if (error) {
    return <p>Error loading about settings. You may need to run the database migration.</p>;
  }

  if (loading && !about) {
    return <p>Loading…</p>;
  }

  return (
    <>
      <SectionTitle>About section</SectionTitle>
      <p style={{ margin: '0 0 1rem', color: 'var(--onSurfaceVariant)', fontSize: '0.9rem' }}>
        Background image and content for the &quot;About NCOF&quot; block on the homepage. This text also appears in that section.
      </p>
      <FormField>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Background image</label>
        <AddRow>
          <UploadButtonLabel className={updateLoading ? 'disabled' : ''}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleImageChange}
              disabled={updateLoading}
            />
            Upload image
          </UploadButtonLabel>
          {uploadError && <span style={{ color: 'var(--error)', fontSize: '0.9rem' }}>{uploadError}</span>}
        </AddRow>
        {aboutImageUrl && (
          <div style={{ marginTop: '0.75rem' }}>
            <img
              src={aboutImageUrl}
              alt="About section preview"
              style={{ maxWidth: '100%', height: 'auto', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }}
            />
          </div>
        )}
      </FormField>
      <FormField>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Homepage blurb (About NCOF text)</label>
        <TextArea
          value={homeBlurb}
          onChange={(e) => setHomeBlurb(e.target.value)}
          placeholder="Welcome to the New Church of Faith…"
        />
      </FormField>
      <AddRow>
        <SaveButton onClick={handleSave} disabled={updateLoading}>
          {updateLoading ? 'Saving…' : 'Save'}
        </SaveButton>
        {saveMessage && <span style={{ color: saveMessage.startsWith('About') ? 'var(--success)' : 'var(--error)', fontSize: '0.9rem' }}>{saveMessage}</span>}
      </AddRow>
    </>
  );
}

function SeriesTab() {
  const { data: seriesList } = useSermonSeries();
  const { featuredSeriesId, loading, error, setFeaturedSeries, setLoading } = useWebsiteFeaturedSeries();
  const [selectedId, setSelectedId] = useState<string | ''>(featuredSeriesId ?? '');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  React.useEffect(() => {
    setSelectedId(featuredSeriesId ?? '');
  }, [featuredSeriesId]);

  const handleSave = async () => {
    setSaveMessage(null);
    try {
      await setFeaturedSeries(selectedId || null);
      setSaveMessage('Featured series updated.');
    } catch (err) {
      console.error(err);
      setSaveMessage('Failed to save.');
    }
  };

  if (error) {
    return <p>Error loading featured series. You may need to run the database migration.</p>;
  }

  return (
    <>
      <SectionTitle>Series section</SectionTitle>
      <p style={{ margin: '0 0 1rem', color: 'var(--onSurfaceVariant)', fontSize: '0.9rem' }}>
        Choose which sermon series appears in the Series section on the public site homepage. It will show that series&apos;s title, graphic, and sermons.
      </p>
      <FormField>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Featured series</label>
        <Select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          style={{ maxWidth: 400 }}
          disabled={loading}
        >
          <option value="">Latest series (default)</option>
          {(seriesList ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </Select>
      </FormField>
      <AddRow>
        <SaveButton onClick={handleSave} disabled={setLoading}>
          {setLoading ? 'Saving…' : 'Save'}
        </SaveButton>
        {saveMessage && (
          <span style={{ color: saveMessage.startsWith('Featured') ? 'var(--success)' : 'var(--error)', fontSize: '0.9rem' }}>
            {saveMessage}
          </span>
        )}
      </AddRow>
    </>
  );
}

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  font-size: 0.95rem;
  width: 100%;
`;

const MinistryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const MinistryLabel = styled.span`
  min-width: 180px;
  font-weight: 600;
  color: var(--onSurface);
`;

function MinistriesTab() {
  const { imagesByKey, loading, error, setImage, setImageLoading } = useWebsiteMinistryImages();
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleFileChange = async (key: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    if (!file.type.startsWith('image/') || !ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Please choose a JPEG, PNG, WebP, or GIF image.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('Image must be smaller than 5MB.');
      e.target.value = '';
      return;
    }
    setUploadingKey(key);
    try {
      const url = await uploadMinistryImage(file);
      await setImage({ key, url });
    } catch (err) {
      console.error(err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploadingKey(null);
      e.target.value = '';
    }
  };

  const handleClear = async (key: string) => {
    setUploadError(null);
    try {
      await setImage({ key, url: null });
    } catch (err) {
      console.error(err);
      setUploadError('Failed to clear.');
    }
  };

  if (error) {
    return <p>Error loading ministry images. You may need to run the database migration.</p>;
  }

  return (
    <>
      <SectionTitle>Ministries images</SectionTitle>
      <p style={{ margin: '0 0 1rem', color: 'var(--onSurfaceVariant)', fontSize: '0.9rem' }}>
        Upload images for each ministry section on the public site Ministries page. Used for the main sections (Men&apos;s, Women&apos;s, Children&apos;s, Teen) and the &quot;More Ministries&quot; grid. JPEG, PNG, WebP, or GIF, max 5MB.
      </p>
      {uploadError && (
        <p style={{ margin: '0 0 1rem', color: 'var(--error)', fontSize: '0.9rem' }}>{uploadError}</p>
      )}
      {loading ? (
        <p>Loading…</p>
      ) : (
        <ImageList>
          {MINISTRY_IMAGE_KEYS.map(({ key, label }) => {
            const row = imagesByKey.get(key);
            const url = row?.url ?? null;
            const isUploading = uploadingKey === key || setImageLoading;
            return (
              <MinistryRow key={key}>
                <MinistryLabel>{label}</MinistryLabel>
                {url ? (
                  <Thumb $src={url} />
                ) : (
                  <div style={{ width: 120, height: 68, background: 'var(--outline)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', color: 'var(--onSurfaceVariant)' }}>
                    No image
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <UploadButtonLabel className={isUploading ? 'disabled' : ''}>
                    <input
                      ref={(el) => { fileInputRefs.current[key] = el; }}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(e) => handleFileChange(key, e)}
                      disabled={!!isUploading}
                    />
                    {uploadingKey === key ? 'Uploading…' : url ? 'Replace' : 'Upload'}
                  </UploadButtonLabel>
                  {url && (
                    <DeleteButton type="button" onClick={() => handleClear(key)} disabled={setImageLoading}>
                      Clear
                    </DeleteButton>
                  )}
                </div>
                {url && (
                  <ImageUrl href={url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, maxWidth: 280 }}>
                    {url}
                  </ImageUrl>
                )}
              </MinistryRow>
            );
          })}
        </ImageList>
      )}
    </>
  );
}

export default function WebsitePage() {
  const tabItems: TabsProps['items'] = [
    { key: 'hero', label: 'Hero images', children: <HeroImagesTab /> },
    { key: 'about', label: 'About', children: <AboutTab /> },
    { key: 'series', label: 'Series', children: <SeriesTab /> },
    { key: 'ministries', label: 'Ministries', children: <MinistriesTab /> },
  ];

  return (
    <Page>
      <h1 style={{ margin: '0 0 1rem', fontSize: '1.5rem', color: 'var(--onSurface)' }}>Website</h1>
      <p style={{ margin: '0 0 1.5rem', color: 'var(--onSurfaceVariant)' }}>
        Manage settings for the public website (ncof.tv).
      </p>
      <Tabs items={tabItems} />
    </Page>
  );
}
